import React, { useState, useEffect, useMemo, useRef, useCallback, startTransition } from 'react';
import { DashboardCard, StatsGrid, StatItem, StatusActionButton, SessionList, Session, ActionItem, BottomSheet } from '../../../shared/ui';
import BottomSelectionPanel from '../../../shared/ui/BottomSelectionPanel';
import { useSiteShifts } from '../hooks/useSiteShifts';
import { SessionsV2Filters, SessionV2, SessionActivity, SessionChangeHistoryEvent, sessionsV2Api } from '../../../api/sessionsV2Api';
import { paymentsV2Api } from '../../../api/paymentsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useToast } from '../../../shared/contexts/ToastContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import SiteShiftsFilter from './SiteShiftsFilter';
import CreateSessionV2Modal from './CreateSessionV2Modal';
import EditSessionV2Modal from './EditSessionV2Modal';
import ActivityPaymentsBottomSheet from './ActivityPaymentsBottomSheet';
import PaymentSheetHost from './PaymentSheetHost';
import BonusSheetHost from './BonusSheetHost';
import CompensationBottomSheet from './CompensationBottomSheet';
import { getMediaUrl } from '../../../utils/avatarUtils';
import ImageViewer from '../../profile/ui/ImageViewer';
import FullscreenCardModal from '../../../shared/ui/cards/FullscreenCardModal';
import { parseApiDateTimeUtc, formatHours } from '../../../shared/utils/timeFormatter';
import styles from './SiteShiftsCard.module.css';
import previewStyles from './SiteShiftsCardPreview.module.css';

interface SiteShiftsCardProps {
  siteId: number;
  myOnly?: boolean;
}

function getCurrentMonthRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return { startDate, endDate };
}

function formatMonthTitle(): string {
  const label = new Date().toLocaleDateString('ru-RU', { month: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function shiftCountLabel(count: number): string {
  const n = Math.abs(count);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} смен`;
  if (mod10 === 1) return `${n} смена`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} смены`;
  return `${n} смен`;
}

function formatHoursValue(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

interface DayShiftStats {
  hours: number;
  amount: number;
}

function getDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

function getCurrentWeekDays(): Date[] {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - mondayOffset);
  monday.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);
    return day;
  });
}

function getMonthGridCells(): (Date | null)[] {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  let startOffset = firstDay.getDay();
  startOffset = startOffset === 0 ? 6 : startOffset - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({ length: startOffset }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

function aggregateSessionsByDay(sessions: SessionV2[]): Map<string, DayShiftStats> {
  const map = new Map<string, DayShiftStats>();
  sessions.forEach((session) => {
    const key = getDateKey(parseApiDateTimeUtc(session.timeStart));
    const existing = map.get(key) ?? { hours: 0, amount: 0 };
    existing.hours += session.hours ?? 0;
    existing.amount += session.totalAmount ?? 0;
    map.set(key, existing);
  });
  return map;
}

function formatPreviewHours(hours: number): string {
  if (hours <= 0) return '—';
  return formatHours(hours);
}

function formatPreviewAmount(amount: number, hasFinancialAccess: boolean): string {
  if (!hasFinancialAccess) return '—';
  if (amount <= 0) return '0 ₽';
  return `${Math.round(amount).toLocaleString('ru-RU')} ₽`;
}

const SiteShiftsCard: React.FC<SiteShiftsCardProps> = ({ siteId, myOnly = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [filters, setFilters] = useState<SessionsV2Filters>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenuBar, setShowActionsMenuBar] = useState(false);
  const [showActivityPaymentsSheet, setShowActivityPaymentsSheet] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<number>>(new Set());
  const [compensationSessions, setCompensationSessions] = useState<SessionV2[]>([]);
  const paymentSheetHostRef = useRef<{ open: (sessions: SessionV2[]) => void }>(null);
  const bonusSheetHostRef = useRef<{ open: (sessions: SessionV2[]) => void }>(null);
  const [selectedSession, setSelectedSession] = useState<SessionV2 | null>(null); // Для редактирования
  const [selectedActivity, setSelectedActivity] = useState<SessionActivity | null>(null);
  const [showAdditionalWorkReasonSheet, setShowAdditionalWorkReasonSheet] = useState(false);
  const [additionalWorkReason, setAdditionalWorkReason] = useState<string | null>(null);
  const [additionalWorkReasonLoading, setAdditionalWorkReasonLoading] = useState(false);
  const [additionalWorkReasonActivity, setAdditionalWorkReasonActivity] = useState<{ activityId: number; sessionId: number; typeOfWorkName?: string } | null>(null);
  const [additionalWorkReasonPhotos, setAdditionalWorkReasonPhotos] = useState<Array<{ id: number; sizes: Array<{ sizeType: string; url: string }> }>>([]);
  const [additionalWorkPhotoViewerOpen, setAdditionalWorkPhotoViewerOpen] = useState(false);
  const [additionalWorkPhotoViewerIndex, setAdditionalWorkPhotoViewerIndex] = useState(0);
  const [showCommentInfoSheet, setShowCommentInfoSheet] = useState(false);
  const [commentInfoActivity, setCommentInfoActivity] = useState<{ activityId: number; sessionId: number; typeOfWorkName?: string; comment?: string } | null>(null);
  const [commentInfoPhotos, setCommentInfoPhotos] = useState<Array<{ id: number; sizes: Array<{ sizeType: string; url: string }> }>>([]);
  const [commentInfoLoading, setCommentInfoLoading] = useState(false);
  const [commentPhotoViewerOpen, setCommentPhotoViewerOpen] = useState(false);
  const [commentPhotoViewerIndex, setCommentPhotoViewerIndex] = useState(0);
  const [canCreateSession, setCanCreateSession] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [sessionActivities, setSessionActivities] = useState<Record<number, SessionActivity[]>>({});
  const [loadingActivities, setLoadingActivities] = useState<Record<number, boolean>>({});
  const [allFinancialVisible, setAllFinancialVisible] = useState(false);
  const [uncensoredSessionIds, setUncensoredSessionIds] = useState<Set<number>>(new Set());
  const [showDeleteConfirmSheet, setShowDeleteConfirmSheet] = useState(false);
  const [sessionsToDelete, setSessionsToDelete] = useState<SessionV2[]>([]);
  const [showSessionHistorySheet, setShowSessionHistorySheet] = useState(false);
  const [sessionForHistory, setSessionForHistory] = useState<SessionV2 | null>(null);
  const [sessionHistoryEvents, setSessionHistoryEvents] = useState<SessionChangeHistoryEvent[]>([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [sessionHistoryError, setSessionHistoryError] = useState<string | null>(null);
  /** Ключ: "u_${userId}" или "e_${siteExecutorId}" — как в окне оплаты */
  const [unallocatedAdvancesCache, setUnallocatedAdvancesCache] = useState<Record<string, number>>({});
  const [unallocatedAdvancesLoadingKey, setUnallocatedAdvancesLoadingKey] = useState<string | null>(null);
  const loadedSessionsRef = useRef<Set<number>>(new Set());
  const lastTypeOfWorkIdRef = useRef<number | undefined>(undefined);
  const lastDisplayedExecutorKeyRef = useRef<string | null>(null);
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const countersContext = useDashboardCounters();
  const sessionsCount = countersContext?.counters?.sessions?.hasAccess
    ? (countersContext.counters.sessions.count ?? 0)
    : 0;
  const countersLoading = countersContext?.loading ?? false;

  useEffect(() => {
    if (!myOnly || !user?.id) {
      return;
    }

    setFilters((prev) => (
      prev.userId === user.id && prev.siteExecutorId == null
        ? prev
        : { ...prev, userId: user.id, siteExecutorId: undefined }
    ));
  }, [myOnly, user?.id]);

  const monthRange = useMemo(() => getCurrentMonthRange(), []);

  const myOnlyFilters = useMemo((): SessionsV2Filters | undefined => {
    if (!myOnly || !user?.id) return undefined;
    return {
      ...filters,
      userId: user.id,
      siteExecutorId: undefined,
      myOnly: true,
    };
  }, [myOnly, user?.id, filters]);

  const monthPreviewFilters = useMemo((): SessionsV2Filters | undefined => {
    if (!myOnlyFilters) return undefined;
    return {
      ...myOnlyFilters,
      startDate: monthRange.startDate,
      endDate: monthRange.endDate,
    };
  }, [myOnlyFilters, monthRange]);

  const {
    sessions: monthPreviewSessions,
    statistics: monthStatistics,
    loading: monthPreviewLoading,
    error: monthPreviewError,
  } = useSiteShifts({
    siteId,
    filters: monthPreviewFilters,
    enabled: Boolean(myOnly && monthPreviewFilters),
  });

  const today = useMemo(() => new Date(), []);
  const weekDays = useMemo(() => getCurrentWeekDays(), []);
  const monthGridCells = useMemo(() => getMonthGridCells(), []);
  const dayStatsByKey = useMemo(
    () => aggregateSessionsByDay(monthPreviewSessions),
    [monthPreviewSessions],
  );
  const handleToggleMyShiftsCollapse = useCallback(() => {
    startTransition(() => {
      setIsCollapsed((prev) => !prev);
    });
  }, []);

  // Ленивая загрузка: для «Мои смены» — только в fullscreen; иначе при inline-раскрытии
  const { sessions, loading, error, statistics, refresh } = useSiteShifts({
    siteId,
    filters: myOnly ? myOnlyFilters : filters,
    autoRefresh: false,
    enabled: myOnly
      ? Boolean(isFullscreenOpen && myOnlyFilters)
      : (!isCollapsed || isFullscreenOpen),
  });

  const isOwnSessionsList = useMemo(() => {
    if (!myOnly || !user?.id || sessions.length === 0) return false;
    return sessions.every((s) => s.userId === user.id);
  }, [myOnly, user?.id, sessions]);

  const isOwnMonthPreviewSessions = useMemo(() => {
    if (!myOnly || !user?.id || monthPreviewSessions.length === 0) return false;
    return monthPreviewSessions.every((s) => s.userId === user.id);
  }, [myOnly, user?.id, monthPreviewSessions]);

  /** Личные смены: показываем финансы, если API вернул флаг или в списке только свои userId */
  const effectiveHasFinancialAccess = myOnly
    ? (statistics.hasFinancialAccess || isOwnSessionsList)
    : statistics.hasFinancialAccess;

  const monthHasFinancialAccess = myOnly
    ? (monthStatistics.hasFinancialAccess || isOwnMonthPreviewSessions)
    : monthStatistics.hasFinancialAccess;

  // При закрытии fullscreen сбрасываем тяжёлое состояние, чтобы не «тащить» его между открытиями (как другие карточки)
  const prevFullscreenRef = useRef(false);
  useEffect(() => {
    if (prevFullscreenRef.current && !isFullscreenOpen) {
      setSessionActivities({});
      setLoadingActivities({});
      setSelectedSessions(new Set());
      setShowActionsMenuBar(false);
      setSelectedSession(null);
      setAllFinancialVisible(false);
      setUncensoredSessionIds(new Set());
      setUnallocatedAdvancesCache({});
      setUnallocatedAdvancesLoadingKey(null);
      loadedSessionsRef.current.clear();
      lastDisplayedExecutorKeyRef.current = null;
    }
    prevFullscreenRef.current = isFullscreenOpen;
  }, [isFullscreenOpen]);

  // Проверяем права на создание смен только когда карточка открыта (не блокируем открытие модалки)
  useEffect(() => {
    const cardIsOpen = myOnly ? isFullscreenOpen : (!isCollapsed || isFullscreenOpen);
    if (cardIsOpen) {
      const checkPermissions = async () => {
        if (!user) {
          setCanCreateSession(false);
          setPermissionLoading(false);
          return;
        }

        try {
          setPermissionLoading(true);
          const result = await sessionsV2Api.checkCreatePermission(siteId);
          setCanCreateSession(result.canCreate);
        } catch (error) {
          console.error('Error checking create permission:', error);
          setCanCreateSession(false);
        } finally {
          setPermissionLoading(false);
        }
      };

      checkPermissions();
    }
  }, [siteId, user, isCollapsed, isFullscreenOpen, myOnly]);

  const handleFiltersChange = (newFilters: SessionsV2Filters) => {
    const nextFilters = myOnly && user?.id
      ? { ...newFilters, userId: user.id, siteExecutorId: undefined }
      : newFilters;

    if (nextFilters.typeOfWorkId !== filters.typeOfWorkId) {
      setSessionActivities({});
      loadedSessionsRef.current.clear();
    }
    setFilters(nextFilters);
  };

  const handleCreateSession = () => {
    setShowCreateModal(true);
  };

  const handleSessionCreated = () => {
    setShowCreateModal(false);
    refresh(); // Обновляем список смен
  };

  const handleSessionEdited = () => {
    setShowEditModal(false);
    setSelectedSession(null);
    setShowActionsMenuBar(false);
    setSelectedSessions(new Set());
    // Очищаем кэш активностей, чтобы загрузить обновленные данные
    setSessionActivities({});
    loadedSessionsRef.current.clear();
    refresh();
  };

  const handleSessionSelect = useCallback((session: Session | SessionV2) => {
    if (myOnly || !session || !user) {
      return;
    }

    // Обновляем состояние синхронно для мгновенного появления меню
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(session.id)) {
        newSet.delete(session.id);
      } else {
        newSet.add(session.id);
      }
      
      // Показываем меню если есть выделенные смены
      if (newSet.size > 0) {
        setShowActionsMenuBar(true);
      } else {
        setShowActionsMenuBar(false);
      }
      
      return newSet;
    });
  }, [user, myOnly]);

  // Эффект для показа меню при изменении выделенных смен
  useEffect(() => {
    if (myOnly) {
      setShowActionsMenuBar(false);
      return;
    }
    if (selectedSessions.size > 0) {
      setShowActionsMenuBar(true);
    } else {
      setShowActionsMenuBar(false);
    }
  }, [selectedSessions, myOnly]);

  const handleDeleteClick = useCallback(() => {
    if (!user || selectedSessions.size === 0) return;
    const list = sessions.filter(s => selectedSessions.has(s.id));
    setSessionsToDelete(list);
    setShowDeleteConfirmSheet(true);
  }, [user, selectedSessions, sessions]);

  const performDelete = useCallback(async (list: SessionV2[]) => {
    if (list.length === 0) return;
    try {
      await Promise.all(
        list.map(session => sessionsV2Api.deleteSession(siteId, session.id))
      );
      const count = list.length;
      showSuccess(count === 1 ? 'Смена успешно удалена' : `${count} смен успешно удалено`);
      setSelectedSessions(new Set());
      setShowActionsMenuBar(false);
      setShowDeleteConfirmSheet(false);
      setSessionsToDelete([]);
      refresh();
    } catch (err: any) {
      console.error('Error deleting sessions:', err);
      showError(err.response?.data?.message || 'Ошибка удаления смен');
    }
  }, [siteId, showSuccess, showError, refresh]);

  /** Открыть шторку ручной компенсации по выбранным сменам (сумма + комментарий) */
  const handleOpenCompensationSheet = useCallback(() => {
    if (selectedSessions.size === 0) return;
    setCompensationSessions(sessions.filter(s => selectedSessions.has(s.id)));
    queueMicrotask(() => setShowActionsMenuBar(false));
  }, [selectedSessions, sessions]);

  const handleCompensationSuccess = useCallback(() => {
    setCompensationSessions([]);
    setSelectedSessions(new Set());
    refresh();
  }, [refresh]);

  const handleDeleteConfirmClose = useCallback(() => {
    setShowDeleteConfirmSheet(false);
    setSessionsToDelete([]);
  }, []);

  const handleActivityPaymentsClick = (activity: any) => {
    setSelectedActivity(null); // сброс, чтобы показать загрузку при повторном открытии
    setShowActivityPaymentsSheet(true); // sheet открывается сразу
    queueMicrotask(() => setSelectedActivity(activity)); // контент подставляется в следующем тике, данные догружаются внутри
  };

  const handleAdditionalWorkReasonClick = useCallback(async (activity: { id: number; typeOfWorkName?: string }, sessionId: number) => {
    setAdditionalWorkReasonActivity({ activityId: activity.id, sessionId, typeOfWorkName: activity.typeOfWorkName });
    setShowAdditionalWorkReasonSheet(true);
    setAdditionalWorkReason(null);
    setAdditionalWorkReasonPhotos([]);
    setAdditionalWorkReasonLoading(true);
    try {
      const [history, photos] = await Promise.all([
        sessionsV2Api.getActivityAdditionalWorkHistory(siteId, sessionId, activity.id),
        sessionsV2Api.getAdditionalWorkPhotos(siteId, sessionId, activity.id).catch(() => []),
      ]);
      const activeRecord = history.find((h: { revokedAt?: string }) => !h.revokedAt);
      const reason = activeRecord?.addedReason?.trim() || null;
      setAdditionalWorkReason(reason);
      setAdditionalWorkReasonPhotos(photos);
    } catch (err) {
      console.error('Error fetching additional work history:', err);
      setAdditionalWorkReason(null);
      showError('Не удалось загрузить причину');
    } finally {
      setAdditionalWorkReasonLoading(false);
    }
  }, [siteId, showError]);

  const handleCommentClick = useCallback(async (activity: { id: number; typeOfWorkName?: string; comment?: string }, sessionId: number) => {
    setCommentInfoActivity({ activityId: activity.id, sessionId, typeOfWorkName: activity.typeOfWorkName, comment: activity.comment });
    setShowCommentInfoSheet(true);
    setCommentInfoPhotos([]);
    setCommentInfoLoading(true);
    try {
      const photos = await sessionsV2Api.getActivityCommentPhotos(siteId, sessionId, activity.id).catch(() => []);
      setCommentInfoPhotos(photos);
    } catch (err) {
      console.error('Error fetching comment photos:', err);
    } finally {
      setCommentInfoLoading(false);
    }
  }, [siteId]);

  // Загрузка истории изменений смены при открытии листа
  useEffect(() => {
    if (!showSessionHistorySheet || !sessionForHistory) {
      setSessionHistoryEvents([]);
      setSessionHistoryError(null);
      return;
    }
    let cancelled = false;
    setSessionHistoryLoading(true);
    setSessionHistoryError(null);
    sessionsV2Api
      .getSessionChangeHistory(siteId, sessionForHistory.id)
      .then((data) => {
        if (!cancelled) {
          setSessionHistoryEvents(data);
          setSessionHistoryError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSessionHistoryEvents([]);
          setSessionHistoryError(err?.response?.data || 'Не удалось загрузить историю изменений');
        }
      })
      .finally(() => {
        if (!cancelled) setSessionHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [showSessionHistorySheet, sessionForHistory, siteId]);

  // Загружаем активности для всех сессий при изменении списка сессий или фильтра
  useEffect(() => {
    // Если изменился фильтр по виду работ, сбрасываем кэш
    if (lastTypeOfWorkIdRef.current !== filters.typeOfWorkId) {
      setSessionActivities({});
      loadedSessionsRef.current.clear();
      lastTypeOfWorkIdRef.current = filters.typeOfWorkId;
    }

    // Сбрасываем кэш загруженных сессий при изменении списка
    const currentSessionIds = new Set(sessions.map(s => s.id));
    const loadedIds = Array.from(loadedSessionsRef.current);
    loadedIds.forEach(id => {
      if (!currentSessionIds.has(id)) {
        loadedSessionsRef.current.delete(id);
        // Удаляем активности для сессий, которых больше нет в списке
        setSessionActivities(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    });

    const loadAllActivities = async () => {
      const sessionIds = sessions
        .map(s => s.id)
        .filter(id => !loadedSessionsRef.current.has(id) && !loadingActivities[id]);
      
      if (sessionIds.length === 0) return;

      // Помечаем сессии как «в загрузке» в ref, но не показываем спиннер по строкам — иначе после появления списка моргает «Загрузка активностей...»
      sessionIds.forEach(sessionId => loadedSessionsRef.current.add(sessionId));

      try {
        console.log(`🚀 BATCH: Loading activities for ${sessionIds.length} sessions with typeOfWorkId filter:`, filters.typeOfWorkId);
        
        // Пытаемся использовать batch API
        try {
          const activitiesMap = await sessionsV2Api.getBatchSessionActivities(siteId, sessionIds);
          
          // Применяем фильтр по typeOfWorkId на клиенте (если установлен; 0 = «Не указанный вид работы» — активности не фильтруем)
          sessionIds.forEach(sessionId => {
            let activities: SessionActivity[] = activitiesMap.get(sessionId) || [];
            
            if (filters.typeOfWorkId !== undefined && filters.typeOfWorkId !== 0) {
              activities = activities.filter(a => a.typeOfWorkId === filters.typeOfWorkId);
            }
            
            setSessionActivities(prev => ({ ...prev, [sessionId]: activities }));
            console.log(`✅ BATCH: Loaded ${activities.length} activities for session ${sessionId}`);
          });
          
          console.log(`✅ BATCH: Successfully loaded activities for ${sessionIds.length} sessions`);
        } catch (batchError: any) {
          // Fallback на старый метод, если batch API недоступен
          console.warn('⚠️ Batch API not available, using fallback method:', batchError);
          
          if (batchError.response?.status === 404 || batchError.response?.status === 501) {
            console.log(`🔄 Fallback: Loading activities one by one (${sessionIds.length} requests)`);
            
            const loadPromises = sessionIds.map(async (sessionId) => {
              try {
                const activities = await sessionsV2Api.getSessionActivities(siteId, sessionId, filters.typeOfWorkId === 0 ? undefined : filters.typeOfWorkId);
                setSessionActivities(prev => ({ ...prev, [sessionId]: activities }));
                console.log(`Loaded ${activities.length} activities for session ${sessionId}`);
              } catch (error) {
                console.error(`Error loading activities for session ${sessionId}:`, error);
                setSessionActivities(prev => ({ ...prev, [sessionId]: [] }));
              }
            });
            
            await Promise.all(loadPromises);
          } else {
            throw batchError;
          }
        }
      } catch (error) {
        console.error('Error loading activities:', error);
        // Сохраняем пустые массивы при ошибке
        sessionIds.forEach(sessionId => {
          setSessionActivities(prev => ({ ...prev, [sessionId]: [] }));
        });
      } finally {
        // Убираем флаги загрузки
        setLoadingActivities(prev => {
          const next = { ...prev };
          sessionIds.forEach(id => delete next[id]);
          return next;
        });
      }
    };

    loadAllActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, siteId, filters.typeOfWorkId]);

  const handleEdit = useCallback((session: SessionV2) => {
    if (!session || !user) return;

    // Закрываем меню действий и сбрасываем выделение
    setShowActionsMenuBar(false);
    setSelectedSessions(new Set());

    // Открываем модальное окно сразу для мгновенной реакции UI
    setSelectedSession(session);
    setShowEditModal(true);

    // Асинхронно проверяем права — при отсутствии прав закроем модалку и покажем ошибку
    sessionsV2Api.checkEditPermission(siteId, session.id)
      .then(result => {
        if (!result.canEdit) {
          showError('У вас нет прав на редактирование этой смены');
          setShowEditModal(false);
          setSelectedSession(null);
        }
      })
      .catch(error => {
        console.error('Error checking edit permission:', error);
        showError('Ошибка при проверке прав доступа');
        setShowEditModal(false);
        setSelectedSession(null);
      });
  }, [user, siteId, showError]);

  const handleUnsetWorkTypeClick = useCallback(
    (sessionId: number) => {
      const session = sessions.find((s) => s.id === sessionId);
      if (session) handleEdit(session);
    },
    [sessions, handleEdit]
  );

  const handleCloseActionsMenuBar = useCallback(() => {
    setShowActionsMenuBar(false);
    // Сбрасываем выделение при закрытии меню
    setSelectedSessions(new Set());
  }, []);

  // Обработчик отмены выделения (для кнопки крестика)
  const handleCancelSelection = useCallback(() => {
    // Не скрываем меню сразу — даём ActionsMenuBar проиграть анимацию,
    // затем ActionsMenuBar вызовет onClose (handleCloseActionsMenuBar), который скроет меню и очистит выделение.
    // Если ничего не выделено — ничего не делаем
    if (selectedSessions.size === 0) return;

    // Очистка выделения в transition (не блокирует UI). Это позволит SelectionStatusBar оставаться видимой
    // до момента, когда ActionsMenuBar завершит анимацию и вызовет handleCloseActionsMenuBar.
    startTransition(() => {
      setSelectedSessions(new Set());
    });
  }, [selectedSessions]);

  // Используем сохраненный список сессий для оплаты
  // Это предотвращает проблему когда selectedSessions сбрасывается при открытии окна

  // Обработчики для оплаты (универсальные для одной или нескольких смен)
  const handleClosePayment = useCallback(() => {
    setSelectedSessions(new Set());
  }, []);

  const handleSuccessPayment = useCallback(() => {
    setSelectedSessions(new Set());
    setUnallocatedAdvancesCache(prev => ({})); // инвалидируем кеш авансов после оплаты
    refresh();
  }, [refresh]);

  // Запрос нераспределённых авансов — используем siteExecutorId когда доступен (как в окне оплаты), иначе userId
  const requestUnallocatedAdvances = useCallback((filters: { userId?: number; siteExecutorId?: number }, cacheKey: string) => {
    setUnallocatedAdvancesCache((prev) => {
      const next = { ...prev };
      delete next[cacheKey];
      return next;
    });
    setUnallocatedAdvancesLoadingKey(cacheKey);
    paymentsV2Api
      .getSitePaymentsWithRemaining(siteId, filters)
      .then(payments => {
        const sum = payments.reduce((acc, p) => acc + (p.remainingAmount ?? 0), 0);
        setUnallocatedAdvancesCache(prev => ({ ...prev, [cacheKey]: Math.round(sum * 100) / 100 }));
      })
      .catch(err => {
        console.error('Error loading unallocated advances:', err);
        showError('Не удалось загрузить сумму нераспределённых авансов');
      })
      .finally(() => {
        setUnallocatedAdvancesLoadingKey(null);
      });
  }, [siteId, showError]);

  // Сброс кэша нераспределённых авансов при смене выделенного пользователя/исполнителя — чтобы не показывать сумму другого
  useEffect(() => {
    if (selectedSessions.size === 0) {
      lastDisplayedExecutorKeyRef.current = null;
      return;
    }
    const selectedSessionsList = sessions.filter(s => selectedSessions.has(s.id));
    const hasExternal = selectedSessionsList.some(s => s.siteExecutorId && s.siteExecutorId > 0);
    const uniqueSiteExecutorIds = new Set(selectedSessionsList.map(s => s.siteExecutorId).filter((id): id is number => id != null && id > 0));
    const uniqueUserIds = new Set(selectedSessionsList.map(s => s.userId).filter((id): id is number => id != null && id > 0));
    const isSingleExecutor = hasExternal ? uniqueSiteExecutorIds.size === 1 : (uniqueUserIds.size === 1 && Array.from(uniqueUserIds)[0] > 0);
    if (!isSingleExecutor) {
      lastDisplayedExecutorKeyRef.current = null;
      return;
    }
    const first = selectedSessionsList[0];
    const siteExecutorId = first?.siteExecutorId && first.siteExecutorId > 0 && selectedSessionsList.every(s => s.siteExecutorId === first.siteExecutorId) ? first.siteExecutorId : undefined;
    const selectedUserId = siteExecutorId != null ? 0 : Array.from(uniqueUserIds)[0];
    const cacheKey = siteExecutorId != null ? `e_${siteExecutorId}` : `u_${selectedUserId}`;
    if (lastDisplayedExecutorKeyRef.current !== null && lastDisplayedExecutorKeyRef.current !== cacheKey) {
      setUnallocatedAdvancesCache({});
      setUnallocatedAdvancesLoadingKey(null);
    }
    lastDisplayedExecutorKeyRef.current = cacheKey;
  }, [selectedSessions, sessions]);

  // Автозагрузка нераспределённых авансов при каждом выделении смен одного исполнителя (без кеша между выделениями)
  useEffect(() => {
    if (selectedSessions.size === 0) return;
    const selectedSessionsList = sessions.filter(s => selectedSessions.has(s.id));
    const hasExternal = selectedSessionsList.some(s => s.siteExecutorId && s.siteExecutorId > 0);
    const uniqueSiteExecutorIds = new Set(selectedSessionsList.map(s => s.siteExecutorId).filter((id): id is number => id != null && id > 0));
    const uniqueUserIds = new Set(selectedSessionsList.map(s => s.userId).filter((id): id is number => id != null && id > 0));
    const isSingleExecutor = hasExternal ? uniqueSiteExecutorIds.size === 1 : (uniqueUserIds.size === 1 && Array.from(uniqueUserIds)[0] > 0);
    if (!isSingleExecutor) return;
    const first = selectedSessionsList[0];
    const siteExecutorId = first?.siteExecutorId && first.siteExecutorId > 0 && selectedSessionsList.every(s => s.siteExecutorId === first.siteExecutorId) ? first.siteExecutorId : undefined;
    const selectedUserId = siteExecutorId != null ? 0 : Array.from(uniqueUserIds)[0];
    const filters = siteExecutorId != null ? { siteExecutorId } : (selectedUserId > 0 ? { userId: selectedUserId } : {});
    const cacheKey = siteExecutorId != null ? `e_${siteExecutorId}` : `u_${selectedUserId}`;
    if (Object.keys(filters).length === 0) return;
    requestUnallocatedAdvances(filters, cacheKey);
  }, [selectedSessions, sessions, requestUnallocatedAdvances]);

  // Вычисление сумм для выделенных смен
  const selectedSessionsSummary = useMemo(() => {
    if (selectedSessions.size === 0) {
      return undefined;
    }

    const selectedSessionsList = sessions.filter(s => selectedSessions.has(s.id));
    let totalAmount = 0;
    let paidAmount = 0;
    let unpaidBonusAmount = 0;
    let remainingAmount = 0;
    let totalHours = 0;
    let hasFinancialAccess = false;

    selectedSessionsList.forEach(session => {
      const activities = sessionActivities[session.id] || [];
      let sessionHasFinancialData = false;
      
      // Вычисляем общее количество часов
      if (activities.length > 0) {
        // Если есть активности, суммируем часы из активностей
        activities.forEach(activity => {
          if (activity.hours) {
            totalHours += activity.hours;
          }
          if (activity.hasFinancialAccess) {
            hasFinancialAccess = true;
            sessionHasFinancialData = true;
            totalAmount += activity.amount ?? 0;
            paidAmount += activity.paidAmount ?? 0;
            unpaidBonusAmount += activity.unpaidBonusAmount ?? 0;
            remainingAmount += activity.remainingAmount ?? 0;
          }
        });
        
        // Если у активностей нет финансового доступа, проверяем финансовый доступ самой сессии
        if (!sessionHasFinancialData && session.hasFinancialAccess) {
          hasFinancialAccess = true;
          totalAmount += session.totalAmount ?? 0;
          paidAmount += session.paidAmount ?? 0;
          remainingAmount += session.remainingAmount ?? 0;
        }
      } else {
        // Если активностей нет, используем часы и финансовые данные самой сессии
        if (session.hours) {
          totalHours += session.hours;
        }
        if (session.hasFinancialAccess) {
          hasFinancialAccess = true;
          totalAmount += session.totalAmount ?? 0;
          paidAmount += session.paidAmount ?? 0;
          remainingAmount += session.remainingAmount ?? 0;
        }
      }
    });

    // Нормализуем значения для точного сравнения (округляем до 2 знаков)
    totalAmount = Math.round(totalAmount * 100) / 100;
    paidAmount = Math.round(paidAmount * 100) / 100;
    unpaidBonusAmount = Math.round(unpaidBonusAmount * 100) / 100;
    remainingAmount = Math.round(remainingAmount * 100) / 100;

    const hasExternal = selectedSessionsList.some(s => s.siteExecutorId && s.siteExecutorId > 0);
    const uniqueSiteExecutorIds = new Set(selectedSessionsList.map(s => s.siteExecutorId).filter((id): id is number => id != null && id > 0));
    const uniqueUserIds = new Set(selectedSessionsList.map(s => s.userId).filter((id): id is number => id != null && id > 0));
    const isSingleExecutor = hasExternal ? uniqueSiteExecutorIds.size === 1 : (uniqueUserIds.size === 1 && Array.from(uniqueUserIds)[0] > 0);
    const first = selectedSessionsList[0];
    const siteExecutorId = first?.siteExecutorId && first.siteExecutorId > 0 && selectedSessionsList.every(s => s.siteExecutorId === first.siteExecutorId) ? first.siteExecutorId : undefined;
    const selectedUserId = isSingleExecutor ? (siteExecutorId != null ? 0 : Array.from(uniqueUserIds)[0]) : undefined;
    const cacheKey = isSingleExecutor ? (siteExecutorId != null ? `e_${siteExecutorId}` : `u_${selectedUserId}`) : null;

    // Показываем сумму только когда она загружена для текущего выбранного исполнителя (cacheKey)
    const cacheMatchesCurrent = cacheKey != null && lastDisplayedExecutorKeyRef.current === cacheKey;
    const unallocatedSum = cacheMatchesCurrent && cacheKey != null && unallocatedAdvancesCache[cacheKey] !== undefined
      ? unallocatedAdvancesCache[cacheKey]
      : undefined;

    return {
      totalAmount: hasFinancialAccess ? totalAmount : undefined,
      paidAmount: hasFinancialAccess ? paidAmount : undefined,
      unpaidBonusAmount: hasFinancialAccess ? unpaidBonusAmount : undefined,
      remainingAmount: hasFinancialAccess ? remainingAmount : undefined,
      totalHours: totalHours > 0 ? totalHours : undefined,
      hasFinancialAccess,
      selectedUserId,
      unallocatedAdvancesSum: unallocatedSum,
      unallocatedAdvancesLoading: cacheKey != null && unallocatedAdvancesLoadingKey === cacheKey
    };
  }, [selectedSessions, sessions, sessionActivities, unallocatedAdvancesCache, unallocatedAdvancesLoadingKey, requestUnallocatedAdvances]);

  // Подготовка действий для меню в зависимости от количества выделенных смен
  const sessionActions: ActionItem[] = useMemo(() => {
    if (myOnly || selectedSessions.size === 0) return [];

    const selectedSessionsList = sessions.filter(s => selectedSessions.has(s.id));
    const isSingle = selectedSessions.size === 1;
    const singleSession = isSingle ? selectedSessionsList[0] : null;

    const actions: ActionItem[] = [];

    // Для одной смены показываем редактирование и историю изменений
    if (isSingle && singleSession) {
      actions.push({
        id: 'edit',
        label: 'Редактировать',
        icon: 'ri-edit-line',
        onClick: () => handleEdit(singleSession),
        variant: 'primary'
      });
      actions.push({
        id: 'history',
        label: 'История изменений',
        icon: 'ri-history-line',
        onClick: () => {
          setSessionForHistory(singleSession);
          setShowSessionHistorySheet(true);
          queueMicrotask(() => setShowActionsMenuBar(false));
        },
        variant: 'secondary'
      });
    }

    // Проверяем, что все выделенные смены принадлежат одному пользователю
    // Оплата возможна только для смен одного сотрудника
    const uniqueUserIds = new Set(selectedSessionsList.map(s => s.userId));
    const isSameUser = uniqueUserIds.size === 1;

    // Оплата (только если все смены принадлежат одному пользователю)
    if (isSameUser) {
      actions.push({
        id: 'pay',
        label: 'Оплатить',
        icon: 'ri-money-dollar-circle-line',
        onClick: () => {
          paymentSheetHostRef.current?.open(selectedSessionsList);
          queueMicrotask(() => setShowActionsMenuBar(false));
        },
        variant: 'success'
      });
      actions.push({
        id: 'bonus',
        label: 'Премировать',
        icon: 'ri-gift-line',
        onClick: () => {
          bonusSheetHostRef.current?.open(selectedSessionsList);
          queueMicrotask(() => setShowActionsMenuBar(false));
        },
        variant: 'primary'
      });
    }

    // Компенсация за выезд — ручная сумма + комментарий, независимо от того, чьи смены выбраны
    actions.push({
      id: 'compensation',
      label: selectedSessions.size === 1 ? 'Компенсация за выезд' : `Компенсация за выезд (${selectedSessions.size})`,
      icon: 'ri-gas-station-line',
      onClick: handleOpenCompensationSheet,
      variant: 'secondary'
    });

    // Удаление
    actions.push({
      id: 'delete',
      label: isSingle ? 'Удалить' : `Удалить (${selectedSessions.size})`,
      icon: 'ri-delete-bin-line',
      onClick: handleDeleteClick,
      variant: 'danger'
    });

    return actions;
  }, [myOnly, selectedSessions, sessions, handleDeleteClick, handleEdit, handleOpenCompensationSheet]);

  const handlePay = (session: SessionV2) => {
    // Пока не реализовываем функционал оплаты
    console.log('Pay action clicked for session:', session.id);
    // TODO: Реализовать оплату в будущем
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' });

    if (date.toDateString() === today.toDateString()) {
      return `Сегодня, ${weekday}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Вчера, ${weekday}`;
    } else {
      const dateStr = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      return `${dateStr}, ${weekday}`;
    }
  };

  const formatHistoryValue = (fieldName: string, value: string): string => {
    if (fieldName === 'TimeStart' || fieldName === 'TimeEnd') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString('ru-RU', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    if (fieldName === 'AllocatedHours') {
      const n = parseFloat(value);
      if (!Number.isNaN(n)) return `${n} ч`;
    }
    if (fieldName === 'Rate') {
      const n = parseFloat(value);
      if (!Number.isNaN(n)) return n.toFixed(2);
    }
    return value;
  };

  const formatDuration = (hours?: number) => {
    if (!hours) return { hours: '0', minutes: null };
    
    const wholeHours = Math.floor(hours);
    let minutes = Math.round((hours - wholeHours) * 60);
    
    // Если минут получилось 60 или больше, переводим в часы
    if (minutes >= 60) {
      const additionalHours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      const totalHours = wholeHours + additionalHours;
      if (minutes === 0) {
        return { hours: `${totalHours}`, minutes: null };
      }
      return { hours: `${totalHours}`, minutes: `${minutes}` };
    }
    
    if (minutes === 0) {
      return { hours: `${wholeHours}`, minutes: null };
    }
    return { hours: `${wholeHours}`, minutes: `${minutes}` };
  };

  const formatDurationString = (hours?: number) => {
    if (!hours) return '0 ч';
    
    const wholeHours = Math.floor(hours);
    let minutes = Math.round((hours - wholeHours) * 60);
    
    // Если минут получилось 60 или больше, переводим в часы
    if (minutes >= 60) {
      const additionalHours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      const totalHours = wholeHours + additionalHours;
      if (minutes === 0) {
        return `${totalHours} ч`;
      }
      return `${totalHours} ч ${minutes} м`;
    }
    
    if (minutes === 0) {
      return `${wholeHours} ч`;
    }
    return `${wholeHours} ч ${minutes} м`;
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
  };

  // Функция для определения, оплачена ли сессия
  const isSessionPaid = useCallback((session: SessionV2, activities: SessionActivity[]): boolean => {
    // Если нет доступа к финансовым данным, считаем что сессия не оплачена (не можем определить)
    if (!session.hasFinancialAccess) {
      return false;
    }

    // Если у сессии есть активности, проверяем их финансовые данные
    if (activities.length > 0) {
      let totalAmount = 0;
      let paidAmount = 0;
      let remainingAmount = 0;

      activities.forEach(activity => {
        if (activity.hasFinancialAccess) {
          totalAmount += activity.amount ?? 0;
          paidAmount += activity.paidAmount ?? 0;
          remainingAmount += activity.remainingAmount ?? 0;
        }
      });

      // Нормализуем значения для точного сравнения (округляем до 2 знаков)
      const normalizedRemaining = Math.round(remainingAmount * 100) / 100;
      const normalizedPaid = Math.round(paidAmount * 100) / 100;
      const normalizedTotal = Math.round(totalAmount * 100) / 100;

      // Считаем полностью оплаченной, если:
      // 1. Остаток <= 0.01 (учитываем возможные ошибки округления)
      // 2. Или выплачено >= наработано (с учетом округления)
      return normalizedRemaining <= 0.01 || 
             normalizedPaid >= normalizedTotal || 
             (normalizedPaid > 0 && normalizedTotal > 0 && Math.abs(normalizedPaid - normalizedTotal) < 0.01);
    } else {
      // Если активностей нет, проверяем финансовые данные самой сессии
      const remainingAmount = session.remainingAmount ?? 0;
      const paidAmount = session.paidAmount ?? 0;
      const totalAmount = session.totalAmount ?? 0;

      // Нормализуем значения для точного сравнения (округляем до 2 знаков)
      const normalizedRemaining = Math.round(remainingAmount * 100) / 100;
      const normalizedPaid = Math.round(paidAmount * 100) / 100;
      const normalizedTotal = Math.round(totalAmount * 100) / 100;

      // Считаем полностью оплаченной, если:
      // 1. Остаток <= 0.01 (учитываем возможные ошибки округления)
      // 2. Или выплачено >= наработано (с учетом округления)
      return normalizedRemaining <= 0.01 || 
             normalizedPaid >= normalizedTotal || 
             (normalizedPaid > 0 && normalizedTotal > 0 && Math.abs(normalizedPaid - normalizedTotal) < 0.01);
    }
  }, []);

  // Преобразуем сессии в формат для SessionList с применением клиентской фильтрации
  const sessionListData = useMemo(() => {
    let filteredSessions = sessions;

    // Фильтр «Не указанный вид работы» (typeOfWorkId === 0): только смены, где есть активность без вида работ
    // Та же логика, что в «Моя смена» и активных сменах: вид не указан = !typeOfWorkId || typeOfWorkId <= 1
    if (filters.typeOfWorkId === 0) {
      filteredSessions = filteredSessions.filter(session => {
        const activities = sessionActivities[session.id] || [];
        return activities.some(a => !a.typeOfWorkId || a.typeOfWorkId <= 1);
      });
    }

    // Применяем клиентский фильтр по оплате
    if (filters.paymentStatus) {
      filteredSessions = filteredSessions.filter(session => {
        const activities = sessionActivities[session.id] || [];
        const isPaid = isSessionPaid(session, activities);
        
        if (filters.paymentStatus === 'paid') {
          return isPaid;
        } else if (filters.paymentStatus === 'unpaid') {
          return !isPaid;
        }
        return true;
      });
    }

    // Применяем клиентский фильтр по доп.работе
    if (filters.isAdditionalWork !== undefined) {
      filteredSessions = filteredSessions.filter(session => {
        const activities = sessionActivities[session.id] || [];
        // Проверяем, есть ли хотя бы одна активность с флагом isAdditionalWork
        const hasAdditionalWork = activities.some(a => a.isAdditionalWork === true);
        
        if (filters.isAdditionalWork === true) {
          // Показываем смены, у которых есть хотя бы одна активность с доп.работой
          return hasAdditionalWork;
        } else {
          // Показываем смены, у которых нет активностей с доп.работой
          return !hasAdditionalWork;
        }
      });
    }

    return filteredSessions.map(session => {
      // Применяем фильтр по доп.работе к активностям внутри смены (как для вида работы)
      let activities = sessionActivities[session.id] || [];
      if (filters.isAdditionalWork === true) {
        activities = activities.filter(a => a.isAdditionalWork === true);
      } else if (filters.isAdditionalWork === false) {
        activities = activities.filter(a => a.isAdditionalWork !== true);
      }
      const sessionFinancialAccess = myOnly && effectiveHasFinancialAccess && session.userId === user?.id
        ? true
        : session.hasFinancialAccess;

      return {
        id: session.id,
        userId: session.userId,
        userName: session.userName,
        timeStart: session.timeStart,
        timeEnd: session.timeEnd,
        hours: session.hours,
        totalAmount: session.totalAmount,
        paidAmount: session.paidAmount,
        compensationAmount: session.compensationAmount,
        compensationPaidAmount: session.compensationPaidAmount,
        remainingAmount: session.remainingAmount,
        hasFinancialAccess: sessionFinancialAccess,
        activities: activities.map(activity => ({
          id: activity.id,
          typeOfWorkId: activity.typeOfWorkId,
          typeOfWorkName: activity.typeOfWorkName,
          timeStart: activity.timeStart,
          timeEnd: activity.timeEnd,
          hours: activity.hours,
          allocatedHours: activity.allocatedHours,
          sortOrder: activity.sortOrder,
          amount: activity.amount,
          paidAmount: activity.paidAmount,
          bonusPaidAmount: activity.bonusPaidAmount,
          unpaidBonusAmount: activity.unpaidBonusAmount,
          remainingAmount: activity.remainingAmount,
          rate: activity.rate,
          hasFinancialAccess: sessionFinancialAccess || activity.hasFinancialAccess,
          comment: activity.comment,
          isAdditionalWork: activity.isAdditionalWork
        }))
      } as Session;
    });
  }, [
    sessions,
    sessionActivities,
    filters.typeOfWorkId,
    filters.paymentStatus,
    filters.isAdditionalWork,
    isSessionPaid,
    myOnly,
    effectiveHasFinancialAccess,
    user?.id,
  ]);

  // (removed) handleSelectAll - 'Выделить все' кнопка удалена для повышения производительности

  // Цензура финансов: видимость по смене и глобальный переключатель
  const isFinancialVisible = useCallback((sessionId: number) => {
    return allFinancialVisible || uncensoredSessionIds.has(sessionId);
  }, [allFinancialVisible, uncensoredSessionIds]);

  const handleToggleFinancialVisible = useCallback((sessionId: number) => {
    setUncensoredSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  }, []);

  const handleToggleAllFinancialVisible = useCallback(() => {
    setAllFinancialVisible(prev => {
      if (prev) setUncensoredSessionIds(new Set());
      return !prev;
    });
  }, []);

  // Пересчитываем статистику на основе отфильтрованных активностей
  const filteredStatistics = useMemo(() => {
    // Если ни фильтр по виду работ, ни по доп.работе не установлены — используем общую статистику
    if (filters.typeOfWorkId === undefined && filters.isAdditionalWork === undefined) {
      return myOnly
        ? { ...statistics, hasFinancialAccess: effectiveHasFinancialAccess }
        : statistics;
    }

    // Пересчитываем на основе отфильтрованных активностей
    let totalHours = 0;
    let totalAmount = 0;
    let paidAmount = 0;
    let bonusPaidAmount = 0;
    let remainingAmount = 0;
    let hasFinancialAccess = false;
    const sessionIdsWithActivities = new Set<number>();

    Object.entries(sessionActivities).forEach(([sessionIdStr, activities]) => {
      // Фильтр по виду работ: typeOfWorkId === 0 — только активности без вида работ
      if (filters.typeOfWorkId === 0 && !activities.some(a => !a.typeOfWorkId || a.typeOfWorkId <= 1)) {
        return;
      }
      // Применяем фильтр по доп.работе к активностям (как в sessionListData)
      let filteredActivities = activities;
      if (filters.isAdditionalWork === true) {
        filteredActivities = activities.filter(a => a.isAdditionalWork === true);
      } else if (filters.isAdditionalWork === false) {
        filteredActivities = activities.filter(a => a.isAdditionalWork !== true);
      }
      // Фильтр по виду работ: только активности с выбранным typeOfWorkId
      if (filters.typeOfWorkId !== undefined && filters.typeOfWorkId !== 0) {
        filteredActivities = filteredActivities.filter(a => a.typeOfWorkId === filters.typeOfWorkId);
      }
      if (filteredActivities.length > 0) {
        sessionIdsWithActivities.add(Number(sessionIdStr));
        filteredActivities.forEach(activity => {
          if (activity.hours) {
            totalHours += activity.hours;
          }
          if (activity.amount) {
            totalAmount += activity.amount;
          }
          if (activity.paidAmount) {
            paidAmount += activity.paidAmount;
          }
          if (activity.bonusPaidAmount) {
            bonusPaidAmount += activity.bonusPaidAmount;
          }
          if (activity.remainingAmount) {
            remainingAmount += activity.remainingAmount;
          }
          if (activity.hasFinancialAccess) {
            hasFinancialAccess = true;
          }
        });
      }
    });

    const statsHasFinancialAccess = myOnly
      ? (hasFinancialAccess || effectiveHasFinancialAccess)
      : hasFinancialAccess;

    return {
      totalSessions: sessionIdsWithActivities.size,
      totalHours,
      totalAmount,
      paidAmount,
      bonusPaidAmount,
      remainingAmount,
      hasFinancialAccess: statsHasFinancialAccess,
      uniqueUsers: new Set(sessions.filter(s => sessionIdsWithActivities.has(s.id)).map(s => s.userId)).size,
      uniqueTypeOfWorks: filters.typeOfWorkId !== undefined ? 1 : statistics.uniqueTypeOfWorks
    };
  }, [
    sessionActivities,
    filters.typeOfWorkId,
    filters.isAdditionalWork,
    sessions,
    statistics,
    myOnly,
    effectiveHasFinancialAccess,
  ]);

  // Подготовка данных для статистики
  const paidTotal = (filteredStatistics.paidAmount ?? 0) + (filteredStatistics.bonusPaidAmount ?? 0);
  const totalHours = filteredStatistics.totalHours ?? 0;
  const avgHourBySalary = totalHours > 0 && filteredStatistics.hasFinancialAccess
    ? Math.round((filteredStatistics.paidAmount ?? 0) / totalHours * 100) / 100
    : null;
  const avgHourWithBonus = totalHours > 0 && filteredStatistics.hasFinancialAccess
    ? Math.round(paidTotal / totalHours * 100) / 100
    : null;

  const statsData: StatItem[] = [
    {
      id: 'sessions',
      value: filteredStatistics.totalSessions,
      label: 'Смен',
      icon: 'ri-calendar-line',
      color: 'primary'
    },
    {
      id: 'hours',
      value: Math.round(filteredStatistics.totalHours * 10) / 10,
      label: 'Часов',
      icon: 'ri-time-line',
      color: 'muted'
    },
    {
      id: 'earned',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.totalAmount).toLocaleString('ru-RU')
        : '—',
      label: 'Заработано',
      icon: 'ri-coins-line',
      color: 'success' as const
    },
    {
      id: 'paid',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.paidAmount ?? 0).toLocaleString('ru-RU')
        : '—',
      label: 'Выплачено (з/п)',
      icon: 'ri-checkbox-circle-line',
      color: 'primary' as const
    },
    {
      id: 'bonus',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.bonusPaidAmount ?? 0).toLocaleString('ru-RU')
        : '—',
      label: 'Премии',
      icon: 'ri-gift-line',
      color: 'primary' as const
    },
    {
      id: 'paidWithBonus',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(paidTotal).toLocaleString('ru-RU')
        : '—',
      label: 'Выплачено с премиями',
      icon: 'ri-bank-card-line',
      color: 'primary' as const
    },
    {
      id: 'remaining',
      value: filteredStatistics.hasFinancialAccess
        ? Math.round(filteredStatistics.remainingAmount ?? 0).toLocaleString('ru-RU')
        : '—',
      label: 'Остаток',
      icon: 'ri-time-line',
      color: 'warning' as const
    },
    {
      id: 'avgHourSalary',
      value: avgHourBySalary != null
        ? avgHourBySalary.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽'
        : '—',
      label: 'Средний час по ЗП',
      icon: 'ri-money-dollar-circle-line',
      color: 'info' as const
    },
    {
      id: 'avgHourWithBonus',
      value: avgHourWithBonus != null
        ? avgHourWithBonus.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽'
        : '—',
      label: 'Средний час (ЗП + премия)',
      icon: 'ri-money-dollar-circle-line',
      color: 'info' as const
    }
  ];

  const renderMyShiftsDayCell = useCallback((date: Date, label: string, compact = false) => {
    const stats = dayStatsByKey.get(getDateKey(date)) ?? { hours: 0, amount: 0 };
    const isTodayCell = isSameCalendarDay(date, today);
    const hasWork = stats.hours > 0 || stats.amount > 0;

    return (
      <div
        key={getDateKey(date)}
        className={`${previewStyles.dayCell}${isTodayCell ? ` ${previewStyles.dayCellToday}` : ''}${hasWork ? ` ${previewStyles.dayCellActive}` : ''}${compact ? ` ${previewStyles.dayCellCompact}` : ''}`}
      >
        <span className={previewStyles.dayLabel}>{label}</span>
        <span className={previewStyles.dayHours}>{formatPreviewHours(stats.hours)}</span>
        <span className={previewStyles.dayAmount}>
          {formatPreviewAmount(stats.amount, monthHasFinancialAccess)}
        </span>
      </div>
    );
  }, [dayStatsByKey, today, monthHasFinancialAccess]);

  const myShiftsWeekStrip = useMemo(() => (
    <div className={previewStyles.weekStrip}>
      {weekDays.map((date, index) => renderMyShiftsDayCell(date, WEEKDAY_LABELS[index], true))}
    </div>
  ), [weekDays, renderMyShiftsDayCell]);

  const myShiftsMonthGrid = useMemo(() => (
    <div className={previewStyles.monthGrid}>
      {WEEKDAY_LABELS.map((label) => (
        <span key={label} className={previewStyles.monthWeekdayHead}>
          {label}
        </span>
      ))}
      {monthGridCells.map((date, index) => (
        date
          ? renderMyShiftsDayCell(date, String(date.getDate()))
          : <span key={`empty-${index}`} className={previewStyles.monthEmptyCell} aria-hidden />
      ))}
    </div>
  ), [monthGridCells, renderMyShiftsDayCell]);

  const myShiftsMonthSummary = useMemo(() => (
    <div className={previewStyles.monthSummary}>
      <span className={previewStyles.collapsedLabel}>{formatMonthTitle()}</span>
      <span className={previewStyles.collapsedHero}>
        {shiftCountLabel(monthStatistics.totalSessions)}
      </span>
      <span className={previewStyles.collapsedMeta}>
        {formatHoursValue(monthStatistics.totalHours)} ч
        {' · '}
        {monthHasFinancialAccess
          ? `${Math.round(monthStatistics.totalAmount).toLocaleString('ru-RU')} ₽`
          : '—'}
      </span>
    </div>
  ), [
    monthStatistics.totalSessions,
    monthStatistics.totalHours,
    monthStatistics.totalAmount,
    monthHasFinancialAccess,
  ]);

  const myShiftsPreview = useMemo(() => {
    if (monthPreviewLoading) {
      return (
        <div className={previewStyles.collapsedPreview}>
          <span className={previewStyles.collapsedLabel}>{formatMonthTitle()}</span>
          <span className={previewStyles.collapsedSkeleton} aria-hidden />
          <span className={previewStyles.weekSkeleton} aria-hidden />
        </div>
      );
    }

    if (monthPreviewError) {
      return (
        <div className={previewStyles.collapsedPreview}>
          <span className={previewStyles.collapsedMuted}>Не удалось загрузить</span>
        </div>
      );
    }

    return (
      <div className={previewStyles.collapsedPreview}>
        {myShiftsMonthSummary}
        <div
          className={`${previewStyles.calendarSlot} ${!isCollapsed ? previewStyles.calendarSlotExpanded : ''}`}
        >
          <div className={previewStyles.calendarSlotContent}>
            {isCollapsed ? myShiftsWeekStrip : myShiftsMonthGrid}
          </div>
        </div>
      </div>
    );
  }, [
    isCollapsed,
    monthPreviewLoading,
    monthPreviewError,
    myShiftsMonthSummary,
    myShiftsMonthGrid,
    myShiftsWeekStrip,
  ]);

  const handleOpenAllShifts = () => {
    setIsFullscreenOpen(true);
  };

  const headerRefreshButton = (
    <button
      className="card-action-btn"
      onClick={(e) => {
        e.stopPropagation();
        setSessionActivities({});
        loadedSessionsRef.current.clear();
        refresh();
      }}
      disabled={loading}
      title="Обновить данные"
    >
      <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'}></i>
    </button>
  );

  const shiftsBody = (
    <>
      {user && canCreateSession && !myOnly && (
        <div className={styles.createSessionSection}>
          <StatusActionButton
            variant="primary"
            fullWidth
            onClick={handleCreateSession}
            disabled={loading || permissionLoading}
          >
            Создать смену
          </StatusActionButton>
        </div>
      )}

      {/* Статистика */}
      <StatsGrid items={statsData} />

      {/* Фильтры */}
      <SiteShiftsFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        loading={false}
        siteId={siteId}
        myOnly={myOnly}
      />

      {/* Кнопка выделения всех смен и глазик цензуры финансов */}
      {!myOnly && !error && sessionListData.length > 0 && (
        <div className={styles.selectAllSection}>
          <div className={styles.selectAllRow}>
            <button
              type="button"
              className={styles.selectAllButton}
              onClick={() => {
                const allIds = new Set(sessionListData.map(s => s.id));
                const allSelected = allIds.size > 0 && selectedSessions.size === allIds.size;
                setSelectedSessions(allSelected ? new Set() : allIds);
                if (!allSelected) setShowActionsMenuBar(true);
              }}
              title={selectedSessions.size === sessionListData.length ? 'Снять выделение' : 'Выделить все смены'}
            >
              <i className="ri-checkbox-multiple-line" />
              <span>{selectedSessions.size === sessionListData.length && sessionListData.length > 0 ? 'Снять выделение' : 'Выделить все'}</span>
            </button>
            {effectiveHasFinancialAccess && (
              <button
                type="button"
                className={styles.financialCensorGlobalEye}
                onClick={handleToggleAllFinancialVisible}
                title={allFinancialVisible ? 'Скрыть финансы по всем сменам' : 'Показать финансы по всем сменам'}
                aria-label={allFinancialVisible ? 'Скрыть финансы' : 'Показать финансы'}
              >
                <i className={allFinancialVisible ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Список смен */}
      {error && (
        <div className={styles.errorState}>
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
          <button 
            className={styles.retryButton}
            onClick={refresh}
          >
            <i className="ri-refresh-line"></i>
            Повторить
          </button>
        </div>
      )}

      {!error && (
        <SessionList
          sessions={sessionListData}
          siteId={siteId}
          onAdditionalWorkReasonClick={handleAdditionalWorkReasonClick}
          onCommentClick={handleCommentClick}
          onUnsetWorkTypeClick={handleUnsetWorkTypeClick}
          onSessionSelect={myOnly ? undefined : handleSessionSelect}
          selectedSessionIds={myOnly ? undefined : selectedSessions}
          onActivityPaymentsClick={handleActivityPaymentsClick}
          isFinancialVisible={effectiveHasFinancialAccess ? isFinancialVisible : undefined}
          onToggleFinancialVisible={effectiveHasFinancialAccess ? handleToggleFinancialVisible : undefined}
          formatTime={formatTime}
          formatDate={formatDate}
          formatDuration={formatDuration}
          formatAmount={formatAmount}
          loading={loading}
          loadingActivities={loadingActivities}
          emptyState={{
            icon: "ri-calendar-line",
            title: "Смены не найдены",
            subtitle: "Попробуйте изменить фильтры"
          }}
        />
      )}

      {/* Информация о правах доступа */}
      {!loading && !error && sessions.length > 0 && !effectiveHasFinancialAccess && (
        <div className={styles.permissionsInfo}>
          <i className="ri-information-line"></i>
          <span>Финансовая информация скрыта. Обратитесь к администратору для получения доступа.</span>
        </div>
      )}
    </>
  );

  const modalsAndSheets = (
    <>
        <CreateSessionV2Modal
          isOpen={showCreateModal}
          siteId={siteId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSessionCreated}
        />

        {/* Модальное окно редактирования смены */}
        <EditSessionV2Modal
          isOpen={showEditModal}
          session={selectedSession}
          siteId={siteId}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSession(null);
          }}
          onSuccess={handleSessionEdited}
        />

        {/* Объединённая панель действий + статус */}
        <BottomSelectionPanel
          isOpen={!myOnly && (showActionsMenuBar || selectedSessions.size > 0)}
          actions={sessionActions}
          onClose={handleCloseActionsMenuBar}
          onCancel={handleCancelSelection}
          selectionSummary={selectedSessionsSummary}
          formatAmount={formatAmount}
          formatHours={formatDurationString}
        />

        {/* Bottom Sheet с информацией о платежах на активность */}
        <ActivityPaymentsBottomSheet
          isOpen={showActivityPaymentsSheet}
          activity={selectedActivity}
          siteId={siteId}
          onClose={() => {
            setShowActivityPaymentsSheet(false);
            setSelectedActivity(null);
          }}
        />

        {/* Подтверждение удаления смен */}
        <BottomSheet
          isOpen={showDeleteConfirmSheet}
          onClose={handleDeleteConfirmClose}
          title={sessionsToDelete.length === 1 ? 'Удалить смену?' : `Удалить смены (${sessionsToDelete.length})?`}
          titleClassName={styles.deleteConfirmSheetTitle}
          size={sessionsToDelete.length <= 2 ? 'small' : 'medium'}
          showHandle={true}
          allowSwipeToClose={true}
        >
          <div className={styles.deleteConfirmContent}>
            <div className={styles.deleteConfirmDetails}>
              {sessionsToDelete.slice(0, 5).map((session) => {
                const activities = sessionActivities[session.id] ?? [];
                const hoursStr = formatDurationString(session.hours);
                const dateStr = formatDate(session.timeStart);
                const timeStr = session.timeEnd
                  ? `${formatTime(session.timeStart)} — ${formatTime(session.timeEnd)}`
                  : formatTime(session.timeStart);
                const activityWord = activities.length === 1 ? 'активность' : activities.length >= 2 && activities.length <= 4 ? 'активности' : 'активностей';
                const activityNames = activities.slice(0, 3).map(a => a.typeOfWorkName || '—').join(', ');
                const moreCount = activities.length > 3 ? activities.length - 3 : 0;
                const activitySuffix = activities.length > 0
                  ? ' · ' + activities.length + ' ' + activityWord + (activityNames ? ': ' + activityNames + (moreCount > 0 ? ' +' + moreCount : '') : '')
                  : null;
                return (
                  <div key={session.id} className={styles.deleteConfirmSessionRow}>
                    <span className={styles.deleteConfirmSessionUser}>
                      {session.userName || `Пользователь ${session.userId}`}
                    </span>
                    <span className={styles.deleteConfirmSessionMeta}>
                      {dateStr} · {timeStr} · {hoursStr}
                      {activitySuffix}
                    </span>
                  </div>
                );
              })}
              {sessionsToDelete.length > 5 && (
                <div className={styles.deleteConfirmSessionMore}>
                  и ещё {sessionsToDelete.length - 5} смен
                </div>
              )}
            </div>
            <div className={styles.deleteConfirmActions}>
              <StatusActionButton
                type="button"
                variant="primary"
                onClick={handleDeleteConfirmClose}
                fullWidth
                icon="ri-close-line"
              >
                Отмена
              </StatusActionButton>
              <StatusActionButton
                type="button"
                variant="danger"
                onClick={() => performDelete(sessionsToDelete)}
                fullWidth
                icon="ri-delete-bin-line"
              >
                Удалить
              </StatusActionButton>
            </div>
          </div>
        </BottomSheet>

        {/* Bottom Sheet: история изменений смены */}
        <BottomSheet
          isOpen={showSessionHistorySheet}
          onClose={() => {
            setShowSessionHistorySheet(false);
            setSessionForHistory(null);
            setSessionHistoryEvents([]);
            setSessionHistoryError(null);
          }}
          title="История изменений"
          size="auto"
          showHandle={true}
          allowSwipeToClose={true}
        >
          <div className={styles.sessionHistoryContent}>
            {sessionForHistory && (
              <>
                <div className={styles.sessionHistorySummary}>
                  <span className={styles.sessionHistoryUser}>{sessionForHistory.userName || `Пользователь ${sessionForHistory.userId}`}</span>
                  <span className={styles.sessionHistoryMeta}>
                    {formatDate(sessionForHistory.timeStart)} · {formatTime(sessionForHistory.timeStart)}
                    {sessionForHistory.timeEnd ? ` — ${formatTime(sessionForHistory.timeEnd)}` : ''}
                  </span>
                </div>
                {sessionHistoryLoading ? (
                  <div className={styles.sessionHistoryLoading}>
                    <i className="ri-loader-4-line ri-spin" aria-hidden />
                    <span>Загрузка истории...</span>
                  </div>
                ) : sessionHistoryError ? (
                  <p className={styles.sessionHistoryError}>{sessionHistoryError}</p>
                ) : sessionHistoryEvents.length === 0 ? (
                  <p className={styles.sessionHistoryEmpty}>Нет записей об изменениях</p>
                ) : (
                  <ul className={styles.sessionHistoryList}>
                    {sessionHistoryEvents.map((ev, idx) => (
                      <li key={`${ev.kind}-${ev.sessionActivityId ?? 's'}-${ev.occurredAt}-${idx}`} className={styles.sessionHistoryItem}>
                        <div className={styles.sessionHistoryItemHead}>
                          <span className={styles.sessionHistoryItemKind}>
                            {ev.kind === 'Session' ? 'Смена' : 'Активность'}
                            {ev.sessionActivityId != null && ` #${ev.sessionActivityId}`}
                          </span>
                          <span className={styles.sessionHistoryItemType}>
                            {ev.eventType === 'Created' && 'Создание'}
                            {ev.eventType === 'Updated' && 'Изменение'}
                            {ev.eventType === 'Deleted' && 'Удаление'}
                          </span>
                          <span className={styles.sessionHistoryItemTime}>
                            {new Date(ev.occurredAt).toLocaleString('ru-RU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {(ev.userName || ev.userId) && (
                            <span className={styles.sessionHistoryItemUser}>
                              {ev.userName || `Пользователь ${ev.userId}`}
                            </span>
                          )}
                        </div>
                        {ev.details && ev.details.length > 0 && (
                          <ul className={styles.sessionHistoryDetails}>
                            {ev.details.map((d, di) => {
                              const fieldLabel: Record<string, string> = {
                                TimeStart: 'Время начала',
                                TimeEnd: 'Время окончания',
                                Comment: 'Комментарий',
                                TypeOfWorkId: 'Вид работ',
                                Rate: 'Ставка'
                              };
                              const label = fieldLabel[d.fieldName] || d.fieldName;
                              const oldVal = d.oldValue != null && d.oldValue !== '' ? formatHistoryValue(d.fieldName, d.oldValue) : '—';
                              const newVal = d.newValue != null && d.newValue !== '' ? formatHistoryValue(d.fieldName, d.newValue) : '—';
                              return (
                                <li key={di} className={styles.sessionHistoryDetailRow}>
                                  <span className={styles.sessionHistoryDetailField}>{label}</span>
                                  {ev.eventType === 'Created' ? (
                                    <span className={styles.sessionHistoryDetailNew}>{newVal}</span>
                                  ) : ev.eventType === 'Deleted' ? (
                                    <span className={styles.sessionHistoryDetailOld}>{oldVal}</span>
                                  ) : (
                                    <span className={styles.sessionHistoryDetailChange}>
                                      {oldVal} → {newVal}
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </BottomSheet>

        {/* Bottom Sheet: причина пометки «доп.работа» */}
        <BottomSheet
          isOpen={showAdditionalWorkReasonSheet}
          onClose={() => {
            setShowAdditionalWorkReasonSheet(false);
            setAdditionalWorkReason(null);
            setAdditionalWorkReasonActivity(null);
            setAdditionalWorkReasonPhotos([]);
          }}
          title="Почему это доп.работа"
          size="auto"
        >
          <div className={styles.additionalWorkReasonContent}>
            {additionalWorkReasonActivity && (
              <div className={styles.additionalWorkReasonActivity}>
                {additionalWorkReasonActivity.typeOfWorkName || 'Вид работ не указан'}
              </div>
            )}
            {additionalWorkReasonLoading ? (
              <div className={styles.additionalWorkReasonLoading}>
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка...</span>
              </div>
            ) : additionalWorkReason ? (
              <p className={styles.additionalWorkReasonText}>{additionalWorkReason}</p>
            ) : (
              <p className={styles.additionalWorkReasonEmpty}>Причина не указана</p>
            )}
            {additionalWorkReasonPhotos.length > 0 && (
              <div className={styles.additionalWorkReasonPhotos}>
                <div className={styles.additionalWorkReasonPhotosLabel}>Прикреплённые фото</div>
                <div className={styles.additionalWorkReasonPhotosGrid}>
                  {additionalWorkReasonPhotos.map((media, index) => {
                    const url = getMediaUrl(media as any, 'full') || getMediaUrl(media as any, 'avatar');
                    if (!url) return null;
                    return (
                      <button
                        key={media.id}
                        type="button"
                        className={styles.additionalWorkReasonPhotoItem}
                        onClick={() => {
                          setAdditionalWorkPhotoViewerIndex(index);
                          setAdditionalWorkPhotoViewerOpen(true);
                        }}
                      >
                        <img src={url} alt="" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </BottomSheet>

        {/* Слайдер фото доп.работы */}
        {additionalWorkPhotoViewerOpen && additionalWorkReasonPhotos.length > 0 && (() => {
          const photoUrls = additionalWorkReasonPhotos
            .map((m) => getMediaUrl(m as any, 'full') || getMediaUrl(m as any, 'avatar'))
            .filter((u): u is string => !!u);
          if (photoUrls.length === 0) return null;
          return (
            <ImageViewer
              images={photoUrls}
              initialIndex={Math.min(additionalWorkPhotoViewerIndex, photoUrls.length - 1)}
              onClose={() => setAdditionalWorkPhotoViewerOpen(false)}
              onIndexChange={(i) => setAdditionalWorkPhotoViewerIndex(i)}
            />
          );
        })()}

        {/* Bottom Sheet: информация о комментарии к активности (с фото) */}
        <BottomSheet
          isOpen={showCommentInfoSheet}
          onClose={() => {
            setShowCommentInfoSheet(false);
            setCommentInfoActivity(null);
            setCommentInfoPhotos([]);
          }}
          title="Комментарий к активности"
          size="auto"
        >
          <div className={styles.additionalWorkReasonContent}>
            {commentInfoActivity && (
              <div className={styles.additionalWorkReasonActivity}>
                {commentInfoActivity.typeOfWorkName || 'Вид работ не указан'}
              </div>
            )}
            {commentInfoActivity?.comment?.trim() ? (
              <p className={styles.additionalWorkReasonText}>{commentInfoActivity.comment}</p>
            ) : (
              <p className={styles.additionalWorkReasonEmpty}>Комментарий не указан</p>
            )}
            {commentInfoLoading && (
              <div className={styles.additionalWorkReasonLoading}>
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка фото...</span>
              </div>
            )}
            {!commentInfoLoading && commentInfoPhotos.length > 0 && (
              <div className={styles.additionalWorkReasonPhotos}>
                <div className={styles.additionalWorkReasonPhotosLabel}>Прикреплённые фото</div>
                <div className={styles.additionalWorkReasonPhotosGrid}>
                  {commentInfoPhotos.map((media, index) => {
                    const url = getMediaUrl(media as any, 'full') || getMediaUrl(media as any, 'avatar');
                    if (!url) return null;
                    return (
                      <button
                        key={media.id}
                        type="button"
                        className={styles.additionalWorkReasonPhotoItem}
                        onClick={() => {
                          setCommentPhotoViewerIndex(index);
                          setCommentPhotoViewerOpen(true);
                        }}
                      >
                        <img src={url} alt="" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </BottomSheet>

        {/* Слайдер фото комментария */}
        {commentPhotoViewerOpen && commentInfoPhotos.length > 0 && (() => {
          const photoUrls = commentInfoPhotos
            .map((m) => getMediaUrl(m as any, 'full') || getMediaUrl(m as any, 'avatar'))
            .filter((u): u is string => !!u);
          if (photoUrls.length === 0) return null;
          return (
            <ImageViewer
              images={photoUrls}
              initialIndex={Math.min(commentPhotoViewerIndex, photoUrls.length - 1)}
              onClose={() => setCommentPhotoViewerOpen(false)}
              onIndexChange={(i) => setCommentPhotoViewerIndex(i)}
            />
          );
        })()}

        {/* Окно оплаты — открывается через ref для мгновенной реакции без тяжёлого ре-рендера */}
        <PaymentSheetHost
          ref={paymentSheetHostRef}
          siteId={siteId}
          typeOfWorkId={filters.typeOfWorkId}
          isAdditionalWork={filters.isAdditionalWork}
          preloadedActivities={sessionActivities}
          onClose={handleClosePayment}
          onSuccess={handleSuccessPayment}
        />
        {/* Окно премирования по выбранным сменам */}
        <BonusSheetHost
          ref={bonusSheetHostRef}
          siteId={siteId}
          onClose={handleClosePayment}
          onSuccess={handleSuccessPayment}
        />
        {/* Ручная компенсация по выбранным сменам: сумма + комментарий */}
        <CompensationBottomSheet
          isOpen={compensationSessions.length > 0}
          sessions={compensationSessions}
          siteId={siteId}
          onClose={() => setCompensationSessions([])}
          onSuccess={handleCompensationSuccess}
        />
    </>
  );

  return (
    <>
      <DashboardCard
        id={myOnly ? 'my-site-shifts-card' : 'site-shifts-card'}
        title={myOnly ? 'Мои смены' : 'Смены'}
        icon={myOnly ? undefined : 'ri-calendar-line'}
        isCollapsible={true}
        isCollapsed={isCollapsed}
        onToggleCollapse={myOnly ? handleToggleMyShiftsCollapse : () => setIsCollapsed(!isCollapsed)}
        expandMode={myOnly ? 'inline' : 'fullscreen'}
        isFullscreenOpen={myOnly ? undefined : isFullscreenOpen}
        onFullscreenOpenChange={myOnly ? undefined : setIsFullscreenOpen}
        keepCollapsedPreviewWhenExpanded={myOnly}
        collapsedPreview={myOnly ? myShiftsPreview : undefined}
        collapsedPreviewVariant={myOnly ? 'my-shifts' : undefined}
        counters={myOnly ? undefined : (sessionsCount > 0 ? undefined : [0])}
        countersLoading={countersLoading}
        warningCounter={!myOnly && sessionsCount > 0 ? sessionsCount : undefined}
        headerActions={headerRefreshButton}
      >
        {myOnly ? (
          <div className={previewStyles.expandedActions}>
            <StatusActionButton
              variant="secondary"
              fullWidth
              onClick={handleOpenAllShifts}
              icon="ri-arrow-right-line"
            >
              Все смены
            </StatusActionButton>
          </div>
        ) : (
          shiftsBody
        )}
        {modalsAndSheets}
      </DashboardCard>

      {myOnly && (
        <FullscreenCardModal
          isOpen={isFullscreenOpen}
          onClose={() => setIsFullscreenOpen(false)}
          title="Мои смены"
          headerActions={headerRefreshButton}
        >
          {shiftsBody}
        </FullscreenCardModal>
      )}
    </>
  );
};

export default SiteShiftsCard;
